/**
 * The left sidebar.
 *
 * Every reference screen is built around one, and it is what makes twenty-four
 * pages read as one product: the same rail, the same selected state, the same
 * account card, wherever you are.
 *
 * Two sizes. At 1200px and up it is the labelled rail from the references.
 * Below that it becomes an icon rail - the labelled version at 248px is part of
 * what left Chat a 156px conversation at 1092px, and a laptop window should not
 * give a quarter of its width to five words. Phones keep the bottom tab bar.
 *
 * Search and notifications live here too. They were in the phone header but
 * nowhere on desktop, so the bigger screen had fewer ways in, not more.
 */

import { useState } from 'react';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { useMinWidth, RAIL_FULL } from '@/lib/viewport';
import { Avatar } from './Avatar';
import { UserName } from './user';
import { AvailabilitySheet, timeLeft, useMyAvailability } from './Availability';
import { useCrewChat } from '@/stores/crewChat';
import { useUnreadNotifications } from '@/lib/notifications';
import { Tooltip } from './Tooltip';
import { StatusPanel } from './StatusPanel';
import { EmojiText } from './EmojiText';
import { useT } from '@/lib/i18n';

export type SideNavItem = {
  id: string;
  label: string;
  icon: string;
  /** A number worth reading, or 0 for none. */
  badge?: number;
  /** A dot where a count would be meaningless. */
  dot?: boolean;
};

/*
 * Five destinations, in the order every reference shows them.
 *
 * Badges, Crew and the staff console are real pages, but promoting them into
 * the primary rail is what crowded the old header. Search reaches them.
 */
export const SIDE_NAV: SideNavItem[] = [
  { id: 'home', label: 'Home', icon: 'home' },
  { id: 'parties', label: 'Parties', icon: 'squads' },
  { id: 'friends', label: 'Friends', icon: 'friends' },
  { id: 'chat', label: 'Chat', icon: 'chat' },
  { id: 'games', label: 'Games', icon: 'dice' }
];

const ITEM = cn(
  'group relative flex h-[46px] items-center rounded-[calc(12px*var(--radius-mul,1))] no-underline outline-none',
  /* Colour and background only - no movement, no shadow. */
  'transition-[background-color,color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
  'focus-visible:shadow-[0_0_0_2px_var(--accent)]'
);

export function SideNav({ route, counts, onNavigate, onOpenAccount }: {
  route: string;
  counts: { friends: number; invites: number; unread: number };
  onNavigate: (id: string) => void;
  onOpenAccount: () => void;
  onSearch: () => void;
}) {
  const t = useT();
  const me = useSession((s) => s.me);
  const free = useMyAvailability();
  const [availOpen, setAvailOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const wide = useMinWidth(RAIL_FULL);
  /* Auto follows the window, the other two are your decision and stick. */
  const saveSettings = useSession((s) => s.saveSettings);
  const navMode = useSession((s) => s.settings?.navMode) ?? 'auto';
  const full = navMode === 'labels' ? true : navMode === 'icons' ? false : wide;
  const crewUnread = useCrewChat((s) => (me?.crew ? s.unread['cc:' + me.crew.id] ?? 0 : 0));
  const unreadUpdates = useUnreadNotifications();

  const items: SideNavItem[] = SIDE_NAV.map((base) => {
    const n = { ...base, label: t('nav.' + base.id) };
    if (n.id === 'friends') return { ...n, dot: counts.friends > 0 };
    if (n.id === 'parties') return { ...n, badge: counts.invites };
    /* A number where "how many" matters, a dot where "something" is enough. */
    if (n.id === 'chat') return { ...n, badge: counts.unread };
    return n;
  });
  /* Your updates, with a dot while something is unread - as the references draw it. */
  items.push({ id: 'notifications', label: t('nav.notifications'), icon: 'bell', dot: unreadUpdates > 0 });

  const who = me?.displayName || me?.username || 'you';

  return (
    <nav
      data-chrome="rail"
      aria-label="Main"
      /*
        The width is animated, not switched.

        Folding the rail used to be one frame wide and one frame narrow,
        which makes the whole page appear to flinch sideways. Width is not a
        compositor property, so this does lay out while it runs - but it is
        one element changing along one axis for a fifth of a second, which is
        cheap, and the alternative is the jump.

        overflow-hidden is what stops the labels wrapping into two lines on
        the way through the narrow end.
      */
      style={{ width: full ? 248 : 76 }}
      className={cn(
        'flex flex-none flex-col overflow-hidden border-r border-line-soft bg-surface-side',
        'transition-[width] duration-[var(--t-slow)] ease-[var(--ease-out-soft,var(--ease-out))]',
        'motion-reduce:transition-none',
        !full && 'items-center'
      )}
    >
      {/* ---- wordmark and notifications ---- */}
      <div className={cn('flex items-center', full ? 'gap-2 px-4 pt-5 pb-4' : 'flex-col gap-2.5 pt-5 pb-3')}>
        <a
          href="#/home"
          aria-label="Pugo home"
          onClick={(e) => { e.preventDefault(); onNavigate('home'); }}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[calc(10px*var(--radius-mul,1))] px-1 no-underline outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
        >
          {/* Sized up front so it cannot arrive late and push the nav down. */}
          {/* Relative, like every other logo in the app: the desktop build loads from
              file:// with base ./, where an absolute /logo-square.png points at
              the root of the disk and the rail shows a broken image. */}
          <img src="./logo.png" alt="" width={38} height={38} className="flex-none rounded-[calc(12px*var(--radius-mul,1))]" />
          {full ? <span className="text-[22px] font-extrabold tracking-[-.03em] text-ink">pugo</span> : null}
        </a>
      </div>

      {/* Search and the bell moved to the title bar across the top of the
          window, where the references draw them. */}

      {/* ---- destinations ---- */}
      <ul className={cn('m-0 flex list-none flex-col gap-1 p-0', full ? 'px-3' : 'items-center')}>
        {items.map((item) => (
          <li key={item.id}>
            <NavLink item={item} on={route === item.id} full={full} onNavigate={onNavigate} />
          </li>
        ))}
      </ul>

      {/* Labels or not, as a choice. Auto is the old behaviour and stays
          the default; picking either of the others sticks. */}
      <div className={cn('mt-1.5 flex', full ? 'justify-end px-3' : 'justify-center')}>
        <Tooltip content={full ? 'Icons only' : 'Show labels'} side="right">
          <button
            type="button"
            aria-label={full ? 'Show icons only' : 'Show labels'}
            onClick={() => void saveSettings({ navMode: full ? 'icons' : 'labels' })}
            className={cn(
              'grid h-7 w-7 place-items-center rounded-[calc(8px*var(--radius-mul,1))] text-ghost outline-none',
              'transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)]',
              'hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]'
            )}
          >
            <Icon name={full ? 'chevronL' : 'chevronR'} size={14} />
          </button>
        </Tooltip>
      </div>

      {/* ---- the extras: crew. Badges live in the top bar only. ----
          Under their own label, as the Crew reference draws it. Your crew by
          name when you are in one. */}
      <div className={cn('mt-5', full ? 'px-3' : 'flex flex-col items-center')}>
        {full ? (
          <p className="m-0 mb-1.5 px-3 text-[13px] font-semibold text-mute">{t('nav.crew')}</p>
        ) : (
          <div className="mb-2 w-10 border-t border-line-soft" />
        )}
        <ul className={cn('m-0 flex list-none flex-col gap-1 p-0', !full && 'items-center')}>
          {/* Your crew by name, then the way to find one - as the Crew
              reference draws it. */}
          {me?.crew ? (
            <li>
              {/* A red count for unread crew chat - the same number the chat
                  launcher shows, from the server's unread count. */}
              <NavLink item={{ id: 'crews', label: me.crew.name, icon: 'crew', badge: crewUnread }} on={route === 'crews'} full={full} onNavigate={onNavigate} />
            </li>
          ) : null}
          <li>
            <NavLink
              item={{ id: 'crews?find=1', label: me?.crew ? t('nav.discoverCrews') : t('nav.findCrew'), icon: 'compass' }}
              on={!me?.crew && route === 'crews'}
              full={full}
              onNavigate={onNavigate}
            />
          </li>
        </ul>
      </div>

      <div className="flex-1" />

      {/* ---- the account ----
          Settings has no row of its own any more: the gear beside your name
          is the one way in, so the same thing is not offered twice. */}
      <div className={cn('border-t border-line-soft', full ? 'mx-5' : 'w-10')} />

      {/* Your status, when you have asked for it: part of the sidebar rather
          than a window over it, so the line above simply moves up. */}
      {/* Kept mounted so it can fold shut as well as open. It is nothing
          at all until you have pressed your face once. */}
      {me ? (
        <StatusPanel me={me} open={statusOpen} folded={!full} onClose={() => setStatusOpen(false)} />
      ) : null}

      {/*
        Your status, and your account settings, as two controls.

        Pressing your own face used to open your profile - the one card you
        never need, because you already know what it says. What you actually
        change several times an evening is whether people should bother you,
        so that is what opens: the states, how long the choice lasts, the
        line under your name and your own numbers. Your profile is a press
        away inside it.
      */}
      <div className={cn('m-3 flex items-center gap-1', !full && 'flex-col')}>
        <Tooltip content="Your status" side="right" disabled={full || statusOpen}>
          <button
            type="button"
            /* The panel's own outside-press handler skips this row, so a
               second press on it closes rather than closing and reopening. */
            data-status-toggle
            onClick={() => {
              if (!me) return;
              /* Folded or not, this is the status. It used to open your own
                 profile card when the rail was narrow, which is the one
                 thing nobody presses their own face to see. */
              setStatusOpen((v) => !v);
            }}
            aria-expanded={statusOpen}
            aria-label={'Your status, ' + who}
            className={cn(
              'flex min-w-0 flex-1 items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] p-2 text-left outline-none',
              'transition-[background-color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
              'hover:bg-surface-2 focus-visible:shadow-[0_0_0_2px_var(--accent)]'
            )}
          >
            {me ? <Avatar user={me} size={38} presence /> : <span className="h-[38px] w-[38px] flex-none rounded-full bg-surface-3" />}
            {full ? (
              <span className="min-w-0 flex-1">
                {me ? <UserName user={me} profile={false} className="block text-[14px] font-bold text-ink" /> : <span className="block truncate text-[14px] font-bold text-ink">You</span>}
                <PresenceLine />
              </span>
            ) : null}
          </button>
        </Tooltip>
        <Tooltip content={free ? 'Free to play · ' + timeLeft(free.until) : 'Say you are free to play'} side="right">
          <button
            type="button"
            onClick={() => setAvailOpen(true)}
            aria-label={free ? 'You are marked free to play. Change it.' : 'Say you are free to play'}
            className={cn(
              'grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] outline-none',
              'transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
              free ? 'bg-accent text-accent-fg' : 'text-mute hover:bg-surface-2 hover:text-ink'
            )}
          >
            <Icon name="bolt" size={18} />
          </button>
        </Tooltip>
        <Tooltip content={t('nav.accountSettings')} side="right">
          <button
            type="button"
            onClick={onOpenAccount}
            aria-label={t('nav.accountSettings')}
            className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-mute outline-none transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)] hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
          >
            <Icon name="settings" size={18} />
          </button>
        </Tooltip>
      </div>

      {availOpen ? <AvailabilitySheet onClose={() => setAvailOpen(false)} /> : null}

    </nav>
  );
}

function NavLink({ item, on, full, onNavigate }: {
  item: SideNavItem;
  on: boolean;
  full: boolean;
  onNavigate: (id: string) => void;
}) {
  const link = (
    <a
      href={'#/' + item.id}
      aria-current={on ? 'page' : undefined}
      /* In the icon rail the label is not on screen, so it becomes the
         accessible name - with the count, which is part of what it says. */
      aria-label={full ? undefined : item.label + (item.badge ? ', ' + item.badge + ' unread' : '')}
      onClick={(e) => { e.preventDefault(); onNavigate(item.id); }}
      className={cn(
        ITEM,
        full ? 'gap-3 px-3' : 'w-[46px] justify-center',
        /* A filled background, not only a colour change, so the current page
           is findable at a glance. */
        on ? 'bg-accent-soft text-accent' : 'text-dim hover:bg-surface-2 hover:text-ink'
      )}
    >
      <Icon
        name={item.icon}
        size={21}
        className={cn('flex-none transition-colors duration-[var(--t-hover)] ease-[var(--ease)]',
          on ? 'text-accent' : 'text-mute group-hover:text-ink')}
      />
      {full ? <span className="flex-1 text-[15px] font-semibold tracking-[-.01em]">{item.label}</span> : null}

      {item.badge ? (
        <span
          aria-hidden={!full}
          className={cn(
            'flex items-center justify-center rounded-full bg-accent px-1.5 font-bold text-accent-fg tabular-nums',
            full
              ? 'h-[20px] min-w-[20px] text-[11.5px]'
              : 'absolute -top-0.5 -right-0.5 h-[18px] min-w-[18px] text-[10.5px] ring-2 ring-[var(--bg-side)]'
          )}
        >
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      ) : item.dot ? (
        <span
          className={cn('h-[7px] w-[7px] rounded-full bg-good', !full && 'absolute top-2 right-2')}
          role="img"
          aria-label="New"
        />
      ) : null}
    </a>
  );

  return full ? link : <Tooltip content={item.label} side="right">{link}</Tooltip>;
}

/**
 * The line under your name.
 *
 * Reads the live connection before the saved status: "Online" on a screen
 * that is actually disconnected is the one thing this line must never say.
 */
function PresenceLine() {
  const me = useSession((s) => s.me);
  const connection = useSession((s) => s.connection);

  if (connection === 'connecting' || connection === 'reconnecting') {
    return (
      <span className="flex items-center gap-1.5 text-[12.5px] text-warn">
        <span className="h-[7px] w-[7px] flex-none rounded-full bg-warn" />
        Reconnecting
      </span>
    );
  }
  if (connection === 'closed') {
    return (
      <span className="flex items-center gap-1.5 text-[12.5px] text-mute">
        <span className="h-[7px] w-[7px] flex-none rounded-full bg-ghost" />
        Offline
      </span>
    );
  }

  const status = me?.status ?? 'online';
  const tone =
    status === 'dnd' ? 'bg-bad' :
      status === 'idle' ? 'bg-warn' :
        status === 'invisible' || status === 'offline' ? 'bg-ghost' : 'bg-good';
  const label =
    status === 'dnd' ? 'Do not disturb' :
      status === 'idle' ? 'Away' :
        status === 'invisible' ? 'Invisible' : status === 'offline' ? 'Offline' : 'Online';

  /*
   * Your own line, when you have written one.
   *
   * This only ever said Online or Away - the line you had actually set was
   * nowhere in the sidebar, so a status with a custom icon in it looked like
   * it had not saved. Your words win when there are any, with the state left
   * to the dot beside them, and EmojiText draws :name: as the picture rather
   * than as five characters of punctuation.
   */
  const mine = me?.customStatus?.trim();

  return (
    <span className="flex items-center gap-1.5 text-[12.5px] text-mute">
      <span className={cn('h-[7px] w-[7px] flex-none rounded-full', tone)} />
      {mine
        ? <EmojiText text={mine} size={13} className="min-w-0 truncate" />
        : label}
    </span>
  );
}
